/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import {
    DetailsList,
    DetailsListLayoutMode,
    IContextualMenuProps,
    Selection,
    ISelection,
    IColumn,
    SelectionMode,
    CommandBar,
    ICommandBarItemProps,
    IDragDropEvents
} from "@fluentui/react";
import ViewSectionTitle from "./ViewSectionTitle";
import styles from "./Panels/FieldDefinitions.module.css";
import mainStyles from "./MainView.module.css";
import { IMainViewViewModel } from "./MainViewViewModel";
import {
    ControlType,
    FieldUI,
    Heading,
    ISettingGroupDefinition,
    IUIControl,
    IUIControls,
    SectionHeading,
    UIControl
} from "@nuance/hierarchical-settings-ui-domain";
import { FieldDefinitionsCommands, IFieldDefinitionsState } from "./FieldDefinitionsViewModel";
import { IFieldDefinitionsPanelData } from "./Panels";
import * as Panels from "./Panels";

const fieldDefinitionsCommands = new FieldDefinitionsCommands();
const cmds = fieldDefinitionsCommands.Cmds;

const controlTypes = {
    [ControlType.TextField]: "Text field",
    [ControlType.NumberField]: "Spin button",
    [ControlType.CheckboxField]: "Checkbox",
    [ControlType.DropdownField]: "Dropdown field",
    [ControlType.RadioField]: "Radio field",
    [ControlType.ToggleField]: "Toggle field",
    [ControlType.Heading]: "Heading",
    [ControlType.SectionHeading]: "Section"
};

const getEffectiveSettingName = (item: UIControl): string => {
    let result = "--unknown--";
    switch (item.ControlDiscriminator) {
        case ControlType.Heading:
            result = (item as Heading).HeadingId;
            break;

        case ControlType.SectionHeading:
            result = (item as SectionHeading).SectionHeadingId;
            break;

        default:
            result = (item as FieldUI).SettingName;
            break;
    }
    return result;
};

const _fieldDefinitionListColumns: IColumn[] = [
    {
        key: "nameCol",
        name: "Setting name",
        fieldName: "settingType",
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        className: styles.settingName,
        onRender: (item: UIControl) => <>{getEffectiveSettingName(item)}</>
    },
    {
        key: "typeCol",
        name: "Type",
        fieldName: "settingType",
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        onRender: (item: IUIControls) => <>{controlTypes[item.ControlDiscriminator]}</>
    }
];

/**
 * Field definition properties
 */
interface IFieldDefinitionProps {
    mainViewVM: IMainViewViewModel;
    settingDefinition: ISettingGroupDefinition;
    frozenControlGuids?: Set<string>;
}

const FieldDefinitions = (props: IFieldDefinitionProps): JSX.Element => {
    const initialState: IFieldDefinitionsState = {
        currentFieldIndex: -1,
        isFieldDefinitionPanelOpen: false,
        canMoveDown: false,
        canMoveUp: false,
        settingDefinition: props.settingDefinition
    };

    const [state, dispatch] = React.useReducer(fieldDefinitionsCommands.Reducer, initialState);

    React.useEffect(() => {
        props.mainViewVM.dispatch(props.mainViewVM.cmds.definitionChange(state.settingDefinition));
    }, [state.settingDefinition]);

    const onClosePanel = () => {
        dispatch(cmds.cancelFieldDefinitionPanel());
    };

    const onSaveContent = (data: IFieldDefinitionsPanelData) => {
        dispatch(cmds.savePanelData(data));
    };

    const onOpenAdvancedView = (definition: ISettingGroupDefinition) => {
        const mainFrameDispatch = props.mainViewVM.state.mainFrameVM.dispatch;
        const mainFrameCmds = props.mainViewVM.state.mainFrameVM.cmds;
        mainFrameDispatch(mainFrameCmds.openAdvancedView(definition));
    };

    /**
     * Validates the setting name
     * @param proposedName Proposed/new settings name
     * @param originalName Original settings
     * @returns An error message or ""
     */
    const validateSettingName = (proposedName: string, originalName: string): string => {
        return fieldDefinitionsCommands.ValidateSettingName(state, proposedName, originalName);
    };

    const _addControlMenuProps: IContextualMenuProps = {
        items: [
            {
                onClick: () => dispatch(cmds.addControl(ControlType.TextField)),
                key: "add-text-field",
                text: "Text field"
            },
            {
                onClick: () => {
                    dispatch(cmds.addControl(ControlType.DropdownField));
                },
                key: "add-dropdown",
                text: "Dropdown"
            },
            {
                onClick: () => {
                    dispatch(cmds.addControl(ControlType.CheckboxField));
                },
                key: "add-checkbox",
                text: "Checkbox"
            },
            {
                onClick: () => {
                    dispatch(cmds.addControl(ControlType.RadioField));
                },
                key: "add-radio",
                text: "Radio button"
            },
            {
                onClick: () => {
                    dispatch(cmds.addControl(ControlType.NumberField));
                },
                key: "add-spin",
                text: "Spin button"
            },
            {
                onClick: () => {
                    dispatch(cmds.addControl(ControlType.ToggleField));
                },
                key: "add-toggle",
                text: "Toggle"
            },
            {
                onClick: () => {
                    dispatch(cmds.addControl(ControlType.Heading));
                },
                key: "add-section",
                text: "Heading"
            },
            {
                onClick: () => {
                    dispatch(cmds.addControl(ControlType.SectionHeading));
                },
                key: "add-group",
                text: "Section"
            }
        ]
    };

    const selection: ISelection = new Selection({
        selectionMode: SelectionMode.single,
        getKey: item => (item as IUIControl).CtrlGuid, //allows selection to get updated after changes
        onSelectionChanged: () => {
            const selections = selection.getSelectedIndices();
            const index = selections.length !== 0 ? selections[0] : -1;
            dispatch(cmds.fieldSelectionChanged(index));
        }
    });

    const _defaultButtonDefinitions: ICommandBarItemProps[] = [
        {
            key: "add",
            text: "Add Control",
            iconProps: { iconName: "Add" },
            subMenuProps: _addControlMenuProps
        },
        {
            key: "advanced",
            text: "Advanced...",
            iconProps: { iconName: "Checklist" },
            onClick: () => onOpenAdvancedView(state.settingDefinition)
        }
    ];

    const _onSelectedButtonDefinitions: ICommandBarItemProps[] = [
        {
            key: "add",
            text: "Add Control",
            iconProps: { iconName: "Add" },
            subMenuProps: _addControlMenuProps
        },
        {
            key: "edit",
            text: "Edit",
            iconProps: { iconName: "Edit" },
            onClick: () => dispatch(cmds.editControl())
        },
        {
            key: "advanced",
            text: "Advanced...",
            iconProps: { iconName: "Checklist" },
            onClick: () => onOpenAdvancedView(state.settingDefinition)
        },
        {
            key: "moveDown",
            text: "Move down",
            iconProps: { iconName: "Down" },
            onClick: () => dispatch(cmds.moveControlDown()),
            disabled: !state.canMoveDown
        },
        {
            key: "moveUp",
            text: "Move up",
            iconProps: { iconName: "Up" },
            onClick: () => dispatch(cmds.moveControlUp()),
            disabled: !state.canMoveUp
        }
    ];

    /**
     * Configuration for drag and drop events
     */
    const dragDropEvents: IDragDropEvents = {
        canDrop: () => {
            return true;
        },
        canDrag: () => {
            return true;
        },
        onDragEnter: () => {
            return "";
        },
        onDragLeave: () => {
            return;
        },
        onDrop: (item?: IUIControls) => {
            if (item) {
                const index = state.settingDefinition.UI.Controls.indexOf(item); //index of where the dragged item is being dropped
                dispatch(cmds.dragAndDropControl(index));
            }
        },
        onDragStart: () => {
            return;
        },
        onDragEnd: () => {
            return;
        }
    };

    return (
        <>
            <ViewSectionTitle text="Settings definition" />

            <div className={mainStyles.fieldDefGrid}>
                <CommandBar
                    ariaLabel="Setting actions"
                    primaryGroupAriaLabel="Actions"
                    items={
                        state.currentFieldIndex >= 0
                            ? _onSelectedButtonDefinitions
                            : _defaultButtonDefinitions
                    }
                />
            </div>

            <DetailsList
                items={state.settingDefinition.UI.Controls}
                setKey="CtrlGuid"
                getKey={item => (item as IUIControl).CtrlGuid}
                columns={_fieldDefinitionListColumns}
                layoutMode={DetailsListLayoutMode.justified}
                selection={selection}
                selectionPreservedOnEmptyClick={true}
                ariaLabelForSelectionColumn="Toggle item selection"
                ariaLabelForSelectAllCheckbox="Toggle selection of all items"
                checkButtonAriaLabel="Select item"
                selectionMode={SelectionMode.single}
                dragDropEvents={dragDropEvents}
            />

            {/***** Text panel *****/}
            {state.currentItem?.data.definitionType === ControlType.TextField && (
                <Panels.TextPanel
                    common={{
                        ...state.currentItem.common,
                        onClosePanel,
                        onSaveContent,
                        validateSettingName
                    }}
                    data={state.currentItem.data}
                />
            )}

            {/***** Dropdown panel *****/}
            {state.currentItem?.data.definitionType === ControlType.DropdownField && (
                <Panels.DropdownPanel
                    common={{
                        ...state.currentItem.common,
                        onClosePanel,
                        onSaveContent,
                        validateSettingName
                    }}
                    data={state.currentItem.data}
                    frozenControlGuids={props.frozenControlGuids}
                />
            )}

            {/***** Checkbox panel *****/}
            {state.currentItem?.data.definitionType === ControlType.CheckboxField && (
                <Panels.CheckboxPanel
                    common={{
                        ...state.currentItem.common,
                        onClosePanel,
                        onSaveContent,
                        validateSettingName
                    }}
                    data={state.currentItem.data}
                />
            )}

            {/***** Radio panel *****/}
            {state.currentItem?.data.definitionType === ControlType.RadioField && (
                <Panels.RadioPanel
                    common={{
                        ...state.currentItem.common,
                        onClosePanel,
                        onSaveContent,
                        validateSettingName
                    }}
                    data={state.currentItem.data}
                    frozenControlGuids={props.frozenControlGuids}
                />
            )}

            {/***** Spin/number panel *****/}
            {state.currentItem?.data.definitionType === ControlType.NumberField && (
                <Panels.SpinPanel
                    common={{
                        ...state.currentItem.common,
                        onClosePanel,
                        onSaveContent,
                        validateSettingName
                    }}
                    data={state.currentItem.data}
                />
            )}

            {/***** Toggle panel *****/}
            {state.currentItem?.data.definitionType === ControlType.ToggleField && (
                <Panels.TogglePanel
                    common={{
                        ...state.currentItem.common,
                        onClosePanel,
                        onSaveContent,
                        validateSettingName
                    }}
                    data={state.currentItem.data}
                />
            )}

            {/***** heading panel *****/}
            {state.currentItem?.data.definitionType === ControlType.Heading && (
                <Panels.HeadingPanel
                    common={{
                        ...state.currentItem.common,
                        onClosePanel,
                        onSaveContent,
                        validateSettingName
                    }}
                    data={state.currentItem.data}
                />
            )}

            {/***** section heading panel *****/}
            {state.currentItem?.data.definitionType === ControlType.SectionHeading && (
                <Panels.SectionHeadingPanel
                    common={{
                        ...state.currentItem.common,
                        onClosePanel,
                        onSaveContent,
                        validateSettingName
                    }}
                    data={state.currentItem.data}
                />
            )}
        </>
    );
};

export default FieldDefinitions;
